import { EventEmitter, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, first, map, Observable, switchMap, tap } from 'rxjs';

import {
  AccountAccess,
  AccountMemberRole,
  AccountUtil,
  AuthService,
  CHRepository,
  ExperienceLicense,
  isContentHubLicense,
  isConversionLicense,
  isHomeLicense,
  License,
  LicenseType,
  TenantService,
  UserDetails,
  UserService,
  WorkroomsLicense
} from '@celum/authentication';
import { CelumPropertiesProvider } from '@celum/core/configuration';
import { isTruthy } from '@celum/core/rxjs';
import { hasValue } from '@celum/core/util';
import {
  Avatar,
  CONTENT_TAB_ITEM,
  EXPERIENCE_TAB_ITEM,
  FORMATS_TAB_ITEM,
  HOME_TAB_ITEM,
  isContentTabItem,
  isExperienceTabItem,
  isFormatsTabItem,
  isHomeTabItem,
  isLibrariesTabItem,
  isOrganisationDetailsTabItem,
  isWorkroomTabItem,
  Languages,
  LIBRARIES_TAB_ITEM,
  ORGANISATION_DETAILS_TAB_ITEM,
  ServiceTabItem,
  ServiceTabItemConfiguration,
  SimpleContextMenuItem,
  Systembar,
  WORKROOMS_TAB_ITEM
} from '@celum/internal-components/systembar';
import { TranslationHelper } from '@celum/ng2base/translation';
import { BrowserUtil } from '@celum/ng2base/util';
import { Language } from '@celum/shared/domain';

import { SystembarConfiguration } from './systembar.component';

export interface SystembarViewModel {
  systemBar: Systembar;
  user: UserDetails;
  token: string;
  formats?: ServiceTabItem;
  activeFormatsAccountAccesses: AccountAccess[];
  showAccessToOrganisationDetails: boolean;
  hasRequiredLicensesForSBEN: boolean;
}

@Injectable()
export class SystembarService {
  public vm$: Observable<SystembarViewModel>;

  public loadingReady = new EventEmitter<boolean>();

  private configuration: SystembarConfiguration;

  private currentApplicationName = CelumPropertiesProvider.properties.applicationName;

  private readonly translate = inject(TranslationHelper);
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  private activeAccountAccesses$: Observable<AccountAccess[]> = this.authService.isAuthenticated$.pipe(
    isTruthy(),
    switchMap(() => this.userService.activeAccountAccesses$)
  );

  // Formats access enabled for activate Account_Admins that have at least the ContentHub license
  public activeFormatsAccountAccesses$ = this.activeAccountAccesses$.pipe(
    map(accountAccesses =>
      accountAccesses.filter(
        accountAccess =>
          AccountUtil.isActiveAccountAdmin(accountAccess) && accountAccess.licenses.some(license => isConversionLicense(license) && license.active)
      )
    )
  );

  public organisationDetailsAccountAccesses$ = this.activeAccountAccesses$.pipe(
    map(accountAccesses => accountAccesses.filter(accountAccess => AccountUtil.isActiveAccountAdmin(accountAccess)))
  );

  public hasRequiredLicensesForSBEN$ = combineLatest([this.tenantService.currentTenantId$, this.activeAccountAccesses$]).pipe(
    map(([currentTenantId, activeAccountAccesses]) =>
      activeAccountAccesses.some(
        accountAccess =>
          accountAccess.accountId === currentTenantId && accountAccess.licenses.some(license => license.type === LicenseType.WORK && license.active)
      )
    )
  );

  private serviceConfiguration$ = this.activeAccountAccesses$.pipe(
    map(activeAccountAccesses => {
      const currentTenantId = this.tenantService.getCurrentTenantId();
      const hasRequiredLicenseForHome = activeAccountAccesses.some(accountAccess =>
        accountAccess.licenses.some(license => isHomeLicense(license) && license.active)
      );

      const chTabItem = this.createTabItem(
        CONTENT_TAB_ITEM,
        this.getContentHubContextMenuItems(activeAccountAccesses),
        this.currentApplicationName === CONTENT_TAB_ITEM.text.toLowerCase()
      );

      const otherTabItems = [WORKROOMS_TAB_ITEM, LIBRARIES_TAB_ITEM, EXPERIENCE_TAB_ITEM].map(tabItem => {
        const isCurrentApplication = this.currentApplicationName === tabItem.text.toLowerCase();
        return this.createTabItem(
          tabItem,
          this.getContextMenuItems(activeAccountAccesses, isCurrentApplication, currentTenantId, tabItem),
          isCurrentApplication
        );
      });

      const tabItems = [chTabItem, ...otherTabItems];
      if (hasRequiredLicenseForHome) {
        const isHomeCurrentApplication = this.currentApplicationName === HOME_TAB_ITEM.text.toLowerCase();
        const homeTabItem = this.createTabItem(
          HOME_TAB_ITEM,
          this.getHomeContextMenuItems(activeAccountAccesses, isHomeCurrentApplication, this.tenantService.getCurrentTenantId(), HOME_TAB_ITEM),
          isHomeCurrentApplication
        );

        tabItems.unshift(homeTabItem);
      }

      return tabItems;
    })
  );

  public init(configuration?: SystembarConfiguration): void {
    this.configuration = configuration;

    this.vm$ = combineLatest([
      this.serviceConfiguration$.pipe(isTruthy(), first()),
      this.userService.currentUser$.pipe(distinctUntilChanged()),
      this.authService.isAuthenticated$.pipe(
        isTruthy(),
        switchMap(() =>
          this.authService.getAuthResult().pipe(
            isTruthy(),
            map(result => result.idToken) // TODO: Switch to accesstoken with this ticket: https://celum.atlassian.net/browse/SACC-5319
          )
        )
      ),
      this.activeFormatsAccountAccesses$,
      this.organisationDetailsAccountAccesses$,
      this.hasRequiredLicensesForSBEN$
    ]).pipe(
      map(([services, user, token, activeFormatsAccountAccesses, organisationDetailsAccountAccesses, hasRequiredLicensesForSBEN]) => {
        const showAccessToOrganisationDetails = organisationDetailsAccountAccesses?.length > 0 && this.currentApplicationName !== 'sacc';
        const tenantId = this.tenantService.getCurrentTenantId({ fallbackToUrl: true });
        let organisationDetailsTab: ServiceTabItem = undefined;

        if (showAccessToOrganisationDetails) {
          organisationDetailsTab = this.createTabItem(
            ORGANISATION_DETAILS_TAB_ITEM,
            this.getOrganisationDetailsContextMenuItems(organisationDetailsAccountAccesses, false, tenantId, ORGANISATION_DETAILS_TAB_ITEM),
            false
          );
        }

        const formatsFeatureEnabled =
          CelumPropertiesProvider.properties.systemBar.formats?.featureEnabled && user.accountAccesses.some(access => AccountUtil.isActiveAccountAdmin(access));

        const systemBar: Systembar = {
          services,
          avatar: this.getConfigurationForAvatar(configuration),
          helpContextMenuItems: this.getHelpContextMenuItems(this.translate.locale, configuration),
          // Providing no languages will hide the language selector
          languages: CelumPropertiesProvider.properties.systemBar.showLanguageSelectorInAvatarMenu ? this.getLanguages() : undefined,
          user,
          notificationFeatureEnabled: CelumPropertiesProvider.properties.systemBar.notification?.featureEnabled,
          formatsFeatureEnabled,
          accessToOrganisationDetailsEnabled: !formatsFeatureEnabled && showAccessToOrganisationDetails,
          ...(hasValue(organisationDetailsTab) && { organisationDetails: organisationDetailsTab })
        };

        const systembarConfiguration: SystembarViewModel = {
          systemBar,
          user,
          token,
          activeFormatsAccountAccesses,
          showAccessToOrganisationDetails,
          hasRequiredLicensesForSBEN
        };

        if (systemBar.formatsFeatureEnabled && activeFormatsAccountAccesses.length > 0) {
          const formatsTabItem = FORMATS_TAB_ITEM;
          systembarConfiguration.formats = this.createTabItem(
            formatsTabItem,
            this.getFormatsContextMenuItems(
              activeFormatsAccountAccesses,
              this.currentApplicationName === 'sacc',
              this.currentApplicationName === 'formats',
              this.tenantService.getCurrentTenantId(),
              formatsTabItem
            ),
            false
          );
        }

        return systembarConfiguration;
      }),
      tap(() => {
        this.loadingReady.emit(true);
      })
    );
  }

  private getLanguages(): Languages {
    const configuredLanguages = CelumPropertiesProvider.properties.availableLanguages.map(lang => lang as Language);
    const nativeTranslations = TranslationHelper.getUILocalesNativeTranslations(configuredLanguages);

    const availableLanguages = new Map<Language, string>();
    configuredLanguages.forEach(language => availableLanguages.set(language, nativeTranslations[language]));

    return {
      availableLanguages,
      activeLanguageKey: this.translate.locale,
      onClick: (selectedLanguage: string) => this.translate.setLanguage(selectedLanguage)
    };
  }

  private getConfigurationForAvatar(systembarConfiguration?: SystembarConfiguration): Avatar {
    /**
     * Layout of the avatar menu:
     * * [Link to non-admin section of SACC]
     * * [Link(s) provided by application]
     * * [Link to admin section of SACC (if user is CELUM admin)]
     */
    const saccUrl = this.getSaccUrl();
    const items: SimpleContextMenuItem[] = [];
    const url = this.router.routerState.snapshot.url;

    if (this.isSaccAdministrationRoute(this.currentApplicationName, url) || this.currentApplicationName !== 'sacc') {
      items.push({
        text: 'SHARED.SYSTEM_BAR.CLOUD_ACCOUNT',
        onClick: () => window.open(`${saccUrl}/myprofile`, '_blank')
      });
    }

    items.push(...(systembarConfiguration?.itemProvider?.() ?? []));

    if (
      this.userService.getCurrentUser().admin &&
      (!this.isSaccAdministrationRoute(this.currentApplicationName, url) || this.currentApplicationName !== 'sacc')
    ) {
      items.push({
        text: 'SHARED.SYSTEM_BAR.SACC_ADMIN_SECTION',
        onClick: () => window.open(`${saccUrl}/administration/admins`, '_blank')
      });
    }

    return {
      items,
      logoutItem: {
        text: 'COMMON.LOGOUT',
        onClick: () => {
          const logoutRedirectUri = CelumPropertiesProvider.properties.authentication.logoutRedirectUri;
          const tenantId = this.tenantService.getCurrentTenantId({ fallbackToUrl: true });
          this.authService.signOut(tenantId && `${logoutRedirectUri}/tenant/${tenantId}`).subscribe();
        },
        dataComponentId: 'avatar-log-out'
      }
    };
  }

  /**
   * Temporary method to get the correct SACC Url until all applications have updated their property files
   */
  private getSaccUrl(): string {
    return hasValue(CelumPropertiesProvider.properties.saccUrl)
      ? CelumPropertiesProvider.properties.saccUrl
      : CelumPropertiesProvider.properties.authentication.saccUrl.replace('/api', '');
  }

  private getContentHubContextMenuItems(activeAccountAccesses: AccountAccess[] = []): SimpleContextMenuItem[] {
    return (
      activeAccountAccesses
        .flatMap(({ licenses }): License[] => licenses)
        .filter(isContentHubLicense)
        .filter(license => license.active)
        .flatMap((license): CHRepository[] => license.chRepositories)
        // Filter out duplicate entries
        .filter((value, index, array) => index === array.findIndex(item => item.repositoryId === value.repositoryId))
        .sort((repo1, repo2) => (repo1.repositoryId ?? '').localeCompare(repo2.repositoryId))
        .map(chRepository => ({
          text: chRepository.repositoryId,
          onClick: () => {
            this.configuration?.onTenantSwitch?.();
            BrowserUtil.openUrl(chRepository.url);
          }
        }))
    );
  }

  private getContextMenuItems(
    activeAccountAccesses: AccountAccess[],
    isCurrentApplication: boolean,
    currentTenantId: string,
    tabItem: ServiceTabItemConfiguration
  ): SimpleContextMenuItem[] {
    if (!activeAccountAccesses) {
      return [];
    }

    return activeAccountAccesses
      .filter(accountAccesses => this.verifyAccountAccessByRole(accountAccesses, tabItem))
      .map(accountAccess => ({
        accountAccess,
        license: UserService.getValidLicenseByType(accountAccess, tabItem.licenseType, isExperienceTabItem(tabItem))
      }))
      .filter(accountAccessAndLicense => accountAccessAndLicense.license)
      .sort((account1, account2) => account1.accountAccess.accountName.localeCompare(account2.accountAccess.accountName))
      .map(({ accountAccess, license }) => this.getTenantSwitchingContextMenuItems(isCurrentApplication, currentTenantId, accountAccess, license, tabItem));
  }

  private getOrganisationDetailsContextMenuItems(
    activeAccountAccesses: AccountAccess[],
    isCurrentApplication: boolean,
    currentTenantId: string,
    tabItem: ServiceTabItemConfiguration
  ): SimpleContextMenuItem[] {
    if (!activeAccountAccesses) {
      return [];
    }

    return activeAccountAccesses
      .filter(accountAccesses => this.verifyAccountAccessByRole(accountAccesses, tabItem))
      .sort((account1, account2) => account1.accountName.localeCompare(account2.accountName))
      .map(accountAccess => this.getTenantSwitchingContextMenuItems(isCurrentApplication, currentTenantId, accountAccess, undefined, tabItem));
  }

  private getHomeContextMenuItems(
    activeAccountAccesses: AccountAccess[],
    isCurrentApplication: boolean,
    currentTenantId: string,
    tabItem: ServiceTabItemConfiguration
  ): SimpleContextMenuItem[] {
    if (!activeAccountAccesses) {
      return [];
    }

    return activeAccountAccesses
      .map(accountAccess => ({
        accountAccess,
        license: accountAccess.licenses.find(license => isHomeLicense(license) && license.active)
      }))
      .filter(accountAccessAndLicense => accountAccessAndLicense.license)
      .sort((account1, account2) => account1.accountAccess.accountName.localeCompare(account2.accountAccess.accountName))
      .map(({ accountAccess, license }) => this.getTenantSwitchingContextMenuItems(isCurrentApplication, currentTenantId, accountAccess, license, tabItem));
  }

  private getFormatsContextMenuItems(
    activeAccountAccesses: AccountAccess[],
    isSacc: boolean,
    isFormats: boolean,
    currentTenantId: string,
    tabItem: ServiceTabItemConfiguration
  ): SimpleContextMenuItem[] {
    if (!activeAccountAccesses) {
      return [];
    }

    return activeAccountAccesses
      .map(accountAccess => ({
        accountAccess,
        license: accountAccess.licenses.find(license => isConversionLicense(license) && license.active)
      }))
      .filter(
        accountAccessAndLicense => accountAccessAndLicense.license && accountAccessAndLicense.accountAccess.effectiveRole === AccountMemberRole.ACCOUNT_ADMIN
      )
      .sort((account1, account2) => account1.accountAccess.accountName.localeCompare(account2.accountAccess.accountName))
      .map(({ accountAccess, license }) =>
        this.getTenantSwitchingContextMenuItems(false, currentTenantId, accountAccess, license, tabItem, !isFormats, !isSacc)
      );
  }

  private getTenantSwitchingContextMenuItems(
    isCurrentApplication: boolean,
    currentTenantId: string,
    accountAccess: AccountAccess,
    license: License,
    tabItemConfiguration?: ServiceTabItemConfiguration,
    openLinkInNewTab = false,
    markActiveTenant = true
  ): SimpleContextMenuItem {
    let tenantAwareUrl: string;
    if (isCurrentApplication) {
      tenantAwareUrl = `${window.location.origin}/tenant/${accountAccess.accountId}`;
    } else {
      if (isWorkroomTabItem(tabItemConfiguration)) {
        tenantAwareUrl = (license as WorkroomsLicense).wrTenantAwareUrl;
      } else if (isLibrariesTabItem(tabItemConfiguration)) {
        tenantAwareUrl = (license as ExperienceLicense).librariesTenantAwareUrl;
      } else if (isExperienceTabItem(tabItemConfiguration)) {
        tenantAwareUrl = (license as ExperienceLicense).experienceTenantAwareUrl;
      } else if (isFormatsTabItem(tabItemConfiguration)) {
        tenantAwareUrl = `${CelumPropertiesProvider.properties.systemBar.formats?.httpBaseAddress}/tenant/${accountAccess.accountId}`;
      } else if (isHomeTabItem(tabItemConfiguration)) {
        tenantAwareUrl = `${CelumPropertiesProvider.properties.systemBar.home?.httpBaseAddress}/tenant/${accountAccess.accountId}`;
      } else if (isOrganisationDetailsTabItem(tabItemConfiguration)) {
        tenantAwareUrl = `${this.getSaccUrl()}/myaccount/${accountAccess.accountId}`;
      } else {
        throw new Error('Unknown or undefined tab item configuration');
      }
    }

    return {
      text: `${accountAccess.accountName}`,
      dataComponentId: markActiveTenant && accountAccess.accountId === currentTenantId ? 'active-tenant' : '',
      onClick: () => {
        this.configuration?.onTenantSwitch?.();
        BrowserUtil.openUrl(tenantAwareUrl, openLinkInNewTab);
      }
    };
  }

  private createTabItem(
    tabItemConfiguration: ServiceTabItemConfiguration,
    contextMenuItems: SimpleContextMenuItem[],
    isCurrentApplication: boolean
  ): ServiceTabItem {
    const serviceTabItem = new ServiceTabItem({
      ...tabItemConfiguration,
      active: isCurrentApplication,
      selected: isCurrentApplication
    });

    serviceTabItem.contextMenuItems = contextMenuItems;
    if (!contextMenuItems || contextMenuItems.length <= 0) {
      serviceTabItem.onClick = () => {
        let url = tabItemConfiguration.applicationUrl;

        if (tabItemConfiguration.fallbackUrlPerLanguage) {
          url = tabItemConfiguration.fallbackUrlPerLanguage[this.translate.locale].replace('${language}', this.translate.locale);
        } else if (isCurrentApplication) {
          url = window.location.origin;
        }

        BrowserUtil.openUrl(url);
      };
    }

    return serviceTabItem;
  }

  private getHelpContextMenuItems(locale: string, systembarConfiguration?: SystembarConfiguration): SimpleContextMenuItem[] {
    const items = [
      {
        text: 'SHARED.SYSTEM_BAR.HELP.HELP_CENTER',
        onClick: () => BrowserUtil.openUrl(CelumPropertiesProvider.properties.systemBar.helpUrls.helpCenter[locale], true)
      },
      {
        text: 'SHARED.SYSTEM_BAR.HELP.TERMS_CONDITIONS',
        onClick: () => BrowserUtil.openUrl(CelumPropertiesProvider.properties.systemBar.helpUrls.termsConditions[locale], true)
      },
      {
        text: 'SHARED.SYSTEM_BAR.HELP.PRIVACY_POLICY',
        onClick: () => BrowserUtil.openUrl(CelumPropertiesProvider.properties.systemBar.helpUrls.privacyPolicy[locale], true)
      }
    ];

    return systembarConfiguration?.helpItemProvider?.(items) ?? items;
  }

  private verifyAccountAccessByRole(accountAccess: AccountAccess, tabItem: ServiceTabItemConfiguration): boolean {
    if (isContentTabItem(tabItem)) {
      return true;
    } else if (isWorkroomTabItem(tabItem)) {
      return [AccountMemberRole.ACCOUNT_ADMIN, AccountMemberRole.CURATOR, AccountMemberRole.PARTICIPANT].includes(accountAccess.effectiveRole);
    } else if (isExperienceTabItem(tabItem)) {
      return true;
    } else if (isLibrariesTabItem(tabItem)) {
      return [AccountMemberRole.ACCOUNT_ADMIN, AccountMemberRole.CURATOR].includes(accountAccess.effectiveRole);
    } else if (isOrganisationDetailsTabItem(tabItem)) {
      return accountAccess.effectiveRole === AccountMemberRole.ACCOUNT_ADMIN;
    } else {
      console.warn('SystembarService: Tried to check role of unknown application:', tabItem);
      return false;
    }
  }

  private isSaccAdministrationRoute(currentApplicationName: string, url: string): boolean {
    return currentApplicationName === 'sacc' && url.startsWith('/administration/');
  }
}
